/**
 * `useRunTranscript(runId)`: a live TranscriptView for one run (snapshot + tail, newest turns).
 * `useRunMaterial(runId)`: the run's materialized state only — what list surfaces render; it
 * never subscribes to the run's events.
 *
 * The store (and its cursor) outlives the component, so leaving and re-opening a session
 * resumes from the last rendered seq. Opening first seeds the store's materialized state (sealed
 * local cache, then the daemon's snapshot) and subscribes from its window cut, so nothing
 * replays from event 0. Live batches are coalesced per animation frame; replay batches flush
 * immediately so a session opens in one render.
 */
import type { TranscriptEvent } from '@viko/protocol'
import { type OlderTurns, openAsks, type TranscriptView } from '@viko/transcript-ui'
import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { useBackend } from './backend.js'
import type { BatchMeta } from './hub.js'
import { RafBatcher } from './raf-batcher.js'

export function useRunTranscript(runId: string): TranscriptView {
  return useRunWindow(runId).view
}

/** The live transcript plus its "earlier turns" control. */
export function useRunWindow(runId: string): { view: TranscriptView; older?: OlderTurns } {
  const b = useBackend()
  const accountId = b.artifactAccess.accountId
  const store = useMemo(() => b.transcriptStore(runId, accountId), [b, runId, accountId])
  useEffect(() => {
    const batcher = new RafBatcher<{ events: TranscriptEvent[]; meta: BatchMeta }>((batches) =>
      store.applyEvents(batches),
    )
    store.setConnection(b.hub.connection)
    let readTimer: ReturnType<typeof setTimeout> | undefined
    let off: (() => void) | undefined
    let alive = true
    void store.ready().then(() => {
      if (!alive) return
      off = b.hub.subscribe(runId, store.cursor(), {
        onEvents(events, meta) {
          batcher.push({ events, meta })
          // The last history page renders at once (a session opens in one render); earlier pages
          // coalesce in the store instead of rendering the whole viewer per page.
          if (meta.replay) batcher.drain()
          clearTimeout(readTimer)
          readTimer = setTimeout(
            () => b.hub.markRead(runId, Math.floor(store.snapshot.lastSeq)),
            1500,
          )
        },
        onConnection(state) {
          batcher.drain()
          store.setConnection(state)
        },
      })
    })
    return () => {
      alive = false
      batcher.drain()
      clearTimeout(readTimer)
      off?.()
    }
  }, [b, runId, store])
  const view = useSyncExternalStore(store.subscribe, () => store.snapshot)
  const older = store.older()
  return older ? { view, older } : { view }
}

/** Open asks for list lines: from the materialized state (no turns needed), else the view. */
export function viewAsks(view: TranscriptView): { id: string; text: string }[] {
  const text = view.material?.askText
  if (text) return Object.entries(text).map(([id, t]) => ({ id, text: t }))
  return openAsks(view).map((a) => ({
    id: a.id,
    text:
      a.type === 'question'
        ? (a.questions[0]?.question ?? 'Question')
        : String(a.title ?? a.command ?? 'Approval'),
  }))
}

/** Snapshot-only view of a run (list surfaces): exact run state, no event subscription. */
export function useRunMaterial(runId: string): TranscriptView {
  const b = useBackend()
  const accountId = b.artifactAccess.accountId
  const store = useMemo(() => b.transcriptStore(runId, accountId), [b, runId, accountId])
  useEffect(() => {
    void store.ready()
  }, [store])
  return useSyncExternalStore(store.subscribe, () => store.snapshot)
}
